import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { AgentConfiguratorState, AgentCoordinatorState, AgentWorker, ReminderPromptPosition } from '../../types/agentConfigurator';
import { DEFAULT_FINAL_STEP_PROMPT, normalizeReminderPromptPosition } from '../../types/agentConfigurator';
import '../agent-configurator/AgentConfigurator.css';
import {
  formatConfiguratorModelLabel,
  getAvailableModelsForConfigurator,
  getConfiguratorModelLabels,
} from '../../utils/configuratorModels';
import {
  getGpt5ModelSpec,
  getTemperatureInactiveHint,
  reasoningOptionLabel,
  resolveModelsReasoning,
  shouldShowTemperature,
} from '../../utils/gpt5ConfiguratorModels';
import {
  MAX_AGENT_DESCRIPTION_LENGTH,
  MAX_AGENT_PROMPT_LENGTH,
  MAX_COORDINATOR_PROMPT_LENGTH,
  MAX_VIRTUAL_USER_SYNC_WORKERS,
} from '../../constants/agentFieldLimits';
import { FieldCharCount } from '../ui/FieldCharCount';
import { RagDocIdsField } from '../agent-configurator/RagDocIdsField';
import {
  getDuplicateAgentNameKeys,
  suggestUniqueAgentName,
} from '../../utils/agentNameUniqueness';

const MAX_NAME_LENGTH = 100;
const AGENT_NAME_REGEX = /^[A-Za-z0-9]+$/;
const MAX_TEMPERATURE = 1.0;
const MAX_WORKERS = MAX_VIRTUAL_USER_SYNC_WORKERS;

function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function sanitizeAgentName(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, '').slice(0, MAX_NAME_LENGTH);
}

function normalizeTemperature(temp: number | undefined): number {
  if (typeof temp !== 'number') return 0.7;
  return temp > MAX_TEMPERATURE ? MAX_TEMPERATURE : temp;
}

/** Same RAG toggle styling as Connector `AgentConfigurator` */
function RagToggleRow({ on, onToggle, label = 'Use RAG' }: { on: boolean; onToggle: () => void; label?: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 14,
        color: '#4A6683',
        cursor: 'pointer',
        userSelect: 'none',
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 48,
          height: 24,
          borderRadius: 12,
          background: on ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
          transition: 'background 0.2s ease',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 26 : 2,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#FFFFFF',
            boxShadow: '0 0 0 1px #E3F0FB',
            transition: 'left 0.2s ease',
          }}
        />
      </div>
      <span>{label}</span>
    </div>
  );
}

function createDefaultCoordinator(): AgentCoordinatorState {
  return {
    name: 'AgentCoordinator',
    description: 'Coordinator node that communicates with worker nodes to respond to user messages.',
    prompt: "You are the coordinator of workers. Use them to respond to the user's message.",
    model: 'gpt-4.1-mini',
    temperature: 0.7,
    use_rag: false, // Отключено через feature-flag
    use_ltm: false, // Отключено через feature-flag
  };
}

function createDefaultWorker(): AgentWorker {
  return {
    id: uuid(),
    name: 'Agent',
    description: 'Worker agent',
    prompt: 'You are a worker agent.',
    mode: 'synchronous', // Только синхронные агенты
    model: 'gpt-4.1-mini',
    temperature: 0.7,
    use_rag: false, // Отключено через feature-flag
    use_ltm: false, // Отключено через feature-flag
  };
}

export interface MiniAgentConfiguratorProps {
  value?: AgentConfiguratorState | null;
  onChange: (config: AgentConfiguratorState) => void;
  /** Per-integration model list + defaults (incl. GPT-5), same as Connector configurator. */
  integrationId?: string | null;
}

export const MiniAgentConfigurator: React.FC<MiniAgentConfiguratorProps> = ({
  value,
  onChange,
  integrationId,
}) => {
  const availableModels = useMemo(
    () => getAvailableModelsForConfigurator(integrationId ?? ''),
    [integrationId]
  );
  const modelLabels = useMemo(
    () => getConfiguratorModelLabels(integrationId ?? ''),
    [integrationId]
  );
  const [coordinator, setCoordinator] = useState<AgentCoordinatorState>(() =>
    value?.coordinator
      ? {
          ...createDefaultCoordinator(),
          ...value.coordinator,
          models_reasoning: resolveModelsReasoning(
            value.coordinator.model,
            value.coordinator.models_reasoning
          ),
          use_rag: value.coordinator.use_rag === true,
          use_ltm: value.coordinator.use_ltm === true,
        }
      : createDefaultCoordinator()
  );
  const [workers, setWorkers] = useState<AgentWorker[]>(() => 
    value?.workers 
      ? (value.workers || [])
          .filter((w) => w.mode === 'synchronous')
          .slice(0, MAX_WORKERS)
          .map((w) => ({
            ...createDefaultWorker(),
            ...w,
            models_reasoning: resolveModelsReasoning(w.model, w.models_reasoning),
            mode: 'synchronous' as const,
            use_rag: w.use_rag === true,
            use_ltm: w.use_ltm === true,
          }))
      : []
  );
  const isSyncingRef = useRef(false);

  // Применяем входящее значение только если оно действительно изменилось
  useEffect(() => {
    if (isSyncingRef.current) {
      isSyncingRef.current = false;
      return;
    }
    
    if (value) {
      setCoordinator({
        ...createDefaultCoordinator(),
        ...value.coordinator,
        models_reasoning: resolveModelsReasoning(
          value.coordinator.model,
          value.coordinator.models_reasoning
        ),
        use_rag: value.coordinator.use_rag === true,
        use_ltm: value.coordinator.use_ltm === true,
      });
      const syncWorkers = (value.workers || [])
        .filter((w) => w.mode === 'synchronous')
        .slice(0, MAX_WORKERS)
        .map((w) => ({
          ...createDefaultWorker(),
          ...w,
          models_reasoning: resolveModelsReasoning(w.model, w.models_reasoning),
          mode: 'synchronous' as const,
          use_rag: w.use_rag === true,
          use_ltm: w.use_ltm === true,
        }));
      setWorkers(syncWorkers);
    } else {
      setCoordinator(createDefaultCoordinator());
      setWorkers([]);
    }
  }, [value]);

  // Функция для уведомления об изменениях
  const notifyChange = (
    newCoordinator: AgentCoordinatorState,
    newWorkers: AgentWorker[],
    overrideReminder?: ReminderPromptPosition
  ) => {
    isSyncingRef.current = true;
    onChange({
      ...(value || {}),
      coordinator: newCoordinator,
      workers: newWorkers,
      reminder_prompt_position:
        overrideReminder !== undefined
          ? overrideReminder
          : normalizeReminderPromptPosition(value?.reminder_prompt_position),
    });
  };

  const handleCoordinatorChange = (updates: Partial<AgentCoordinatorState>) => {
    setCoordinator((prev) => {
      const newCoordinator = {
        ...prev,
        ...updates,
      };
      notifyChange(newCoordinator, workers);
      return newCoordinator;
    });
  };

  const handleRagMetaPromptChange = (text: string) => {
    const raw = text.slice(0, MAX_AGENT_PROMPT_LENGTH);
    isSyncingRef.current = true;
    onChange({
      ...(value || {}),
      coordinator,
      workers,
      metaPrompts: {
        ...(value?.metaPrompts || {}),
        RAG_PROMPT: raw.trim() ? raw : undefined,
      },
      reminder_prompt_position: normalizeReminderPromptPosition(value?.reminder_prompt_position),
    });
  };

  const handleFinalStepMetaPromptChange = (text: string) => {
    const raw = text.slice(0, MAX_AGENT_PROMPT_LENGTH);
    isSyncingRef.current = true;
    onChange({
      ...(value || {}),
      coordinator,
      workers,
      metaPrompts: {
        ...(value?.metaPrompts || {}),
        FINAL_STEP_PROMPT: raw.trim() ? raw : undefined,
      },
      reminder_prompt_position: normalizeReminderPromptPosition(value?.reminder_prompt_position),
    });
  };

  const duplicateAgentNameKeys = useMemo(
    () => getDuplicateAgentNameKeys(coordinator.name, workers),
    [coordinator.name, workers]
  );
  const isCoordinatorNameDuplicated = duplicateAgentNameKeys.has(
    String(coordinator.name || '').trim().toLowerCase()
  );

  const handleCreateWorker = () => {
    if (workers.length >= MAX_WORKERS) return;
    setWorkers((prev) => {
      const worker = createDefaultWorker();
      worker.name = suggestUniqueAgentName(coordinator.name, prev);
      const newWorkers = [...prev, worker];
      notifyChange(coordinator, newWorkers);
      return newWorkers;
    });
  };

  const handleUpdateWorker = (id: string, updates: Partial<AgentWorker>) => {
    setWorkers((prev) => {
      const newWorkers = prev.map((w) =>
        w.id === id
          ? {
              ...w,
              ...updates,
              mode: 'synchronous' as const,
            }
          : w
      );
      notifyChange(coordinator, newWorkers);
      return newWorkers;
    });
  };

  const handleDeleteWorker = (id: string) => {
    setWorkers((prev) => {
      const newWorkers = prev.filter((w) => w.id !== id);
      notifyChange(coordinator, newWorkers);
      return newWorkers;
    });
  };

  const reminderPos = normalizeReminderPromptPosition(value?.reminder_prompt_position);

  return (
    <div style={{ marginTop: '16px' }}>
      <div
        style={{
          marginBottom: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#4A6683' }}>Reminder position</span>
        <div
          style={{
            display: 'inline-flex',
            background: '#F5F9FE',
            borderRadius: '999px',
            padding: '2px',
            gap: '2px',
            border: '1px solid #C6E0F7',
          }}
        >
          <button
            type="button"
            onClick={() => notifyChange(coordinator, workers, 'before')}
            style={{
              padding: '5px 12px',
              fontSize: '12px',
              fontWeight: 600,
              border: 'none',
              borderRadius: '999px',
              cursor: 'pointer',
              background: reminderPos === 'before' ? '#4A6683' : 'transparent',
              color: reminderPos === 'before' ? '#FFFFFF' : '#4A6683',
            }}
          >
            Before
          </button>
          <button
            type="button"
            onClick={() => notifyChange(coordinator, workers, 'after')}
            style={{
              padding: '5px 12px',
              fontSize: '12px',
              fontWeight: 600,
              border: 'none',
              borderRadius: '999px',
              cursor: 'pointer',
              background: reminderPos === 'after' ? '#4A6683' : 'transparent',
              color: reminderPos === 'after' ? '#FFFFFF' : '#4A6683',
            }}
          >
            After
          </button>
        </div>
      </div>

      {/* Coordinator */}
      <div className="ac-card" style={{ marginBottom: '16px' }}>
        <div className="ac-card__header">
          <div>
            <div className="ac-badge">Agent Coordinator</div>
            <div className="ac-subtitle">Coordinates workers and responds to the user</div>
          </div>
        </div>

        <div className="ac-grid ac--two">
          <div
            className={`ac-field${isCoordinatorNameDuplicated ? ' ac-field--error' : ''}`}
            style={{ gridColumn: '1 / -1' }}
          >
            <label>Name</label>
            <input
              type="text"
              placeholder="Coordinator name..."
              value={coordinator.name}
              maxLength={MAX_NAME_LENGTH}
              pattern={AGENT_NAME_REGEX.source}
              onChange={(e) => handleCoordinatorChange({ name: sanitizeAgentName(e.target.value) })}
            />
            {isCoordinatorNameDuplicated && (
              <div className="ac-field-error-hint">This name is duplicated. Use a unique agent name.</div>
            )}
            <FieldCharCount length={coordinator.name.length} max={MAX_NAME_LENGTH} />
          </div>

          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <label>Description</label>
            <textarea
              placeholder="Brief description..."
              value={coordinator.description}
              maxLength={MAX_AGENT_DESCRIPTION_LENGTH}
              aria-describedby="mac-coordinator-description-count"
              onChange={(e) =>
                handleCoordinatorChange({ description: e.target.value.slice(0, MAX_AGENT_DESCRIPTION_LENGTH) })
              }
              rows={2}
            />
            <div id="mac-coordinator-description-count">
              <FieldCharCount length={coordinator.description.length} max={MAX_AGENT_DESCRIPTION_LENGTH} />
            </div>
          </div>

          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <label>Prompt</label>
            <textarea
              placeholder="Coordinator instructions..."
              value={coordinator.prompt}
              maxLength={MAX_COORDINATOR_PROMPT_LENGTH}
              aria-describedby="mac-coordinator-prompt-count"
              onChange={(e) =>
                handleCoordinatorChange({ prompt: e.target.value.slice(0, MAX_COORDINATOR_PROMPT_LENGTH) })
              }
              rows={3}
            />
            <div id="mac-coordinator-prompt-count">
              <FieldCharCount length={coordinator.prompt.length} max={MAX_COORDINATOR_PROMPT_LENGTH} />
            </div>
          </div>

          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <label>Reminder prompt</label>
            <textarea
              placeholder={"e.g. Start your reply with the phrase '…'"}
              value={coordinator.reminder_prompt ?? ''}
              maxLength={MAX_AGENT_PROMPT_LENGTH}
              aria-describedby="mac-coordinator-reminder-count"
              onChange={(e) =>
                handleCoordinatorChange({
                  reminder_prompt: e.target.value.slice(0, MAX_AGENT_PROMPT_LENGTH) || undefined,
                })
              }
              rows={2}
            />
            <div id="mac-coordinator-reminder-count">
              <FieldCharCount length={(coordinator.reminder_prompt ?? '').length} max={MAX_AGENT_PROMPT_LENGTH} />
            </div>
          </div>

          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <label>Model</label>
            <select
              value={coordinator.model}
              onChange={(e) => {
                const m = e.target.value;
                handleCoordinatorChange({
                  model: m,
                  models_reasoning: resolveModelsReasoning(m, coordinator.models_reasoning),
                });
              }}
            >
              {availableModels.map((model) => (
                <option key={model} value={model}>
                  {formatConfiguratorModelLabel(model, modelLabels)}
                </option>
              ))}
            </select>
          </div>

          {(() => {
            const spec = getGpt5ModelSpec(coordinator.model);
            if (!spec) return null;
            const mr =
              resolveModelsReasoning(coordinator.model, coordinator.models_reasoning) ?? spec.defaultReasoning;
            return (
              <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                <label>Reasoning</label>
                <p className="ac-gpt5-hint">{spec.hint}</p>
                <select
                  value={mr}
                  onChange={(e) => handleCoordinatorChange({ models_reasoning: e.target.value })}
                >
                  {spec.reasoningOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {reasoningOptionLabel(opt, spec.defaultReasoning)}
                    </option>
                  ))}
                </select>
              </div>
            );
          })()}

          {(() => {
            const coordMr = resolveModelsReasoning(coordinator.model, coordinator.models_reasoning);
            const tempActive = shouldShowTemperature(coordinator.model, coordMr);
            const inactiveHint = getTemperatureInactiveHint(coordinator.model, coordMr);
            if (!getGpt5ModelSpec(coordinator.model)) {
              return (
                <div className="ac-field ac-temperature-field">
                  <label>Temperature</label>
                  <div className="ac-range">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={coordinator.temperature}
                      onChange={(e) =>
                        handleCoordinatorChange({ temperature: normalizeTemperature(parseFloat(e.target.value)) })
                      }
                    />
                    <div className="ac-value">
                      <span>{coordinator.temperature.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div
                className={`ac-field ac-temperature-field${tempActive ? '' : ' ac-temperature-field--inactive'}`}
              >
                <label>
                  Temperature
                  {!tempActive ? <span className="ac-temperature-field__badge">Not active</span> : null}
                </label>
                {!tempActive && inactiveHint ? (
                  <p className="ac-temperature-field__hint">{inactiveHint}</p>
                ) : null}
                <div className="ac-range">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={coordinator.temperature}
                    disabled={!tempActive}
                    onChange={(e) =>
                      handleCoordinatorChange({ temperature: normalizeTemperature(parseFloat(e.target.value)) })
                    }
                  />
                  <div className="ac-value">
                    <span>{coordinator.temperature.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <label
              style={{ fontSize: '14px', fontWeight: 500, color: '#4A6683', marginBottom: '8px', display: 'block' }}
            >
              Tools for Coordinator
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <RagToggleRow
                on={coordinator.use_rag === true}
                onToggle={() => {
                  const next = !coordinator.use_rag;
                  handleCoordinatorChange({
                    use_rag: next,
                    ...(next ? {} : { rag_doc_ids: undefined }),
                  });
                }}
              />
            </div>
            <RagDocIdsField
              integrationId={integrationId}
              useRag={coordinator.use_rag === true}
              ragDocIds={coordinator.rag_doc_ids}
              onChange={(ids) => handleCoordinatorChange({ rag_doc_ids: ids })}
              disabled={false}
              idSuffix={`coord-${coordinator.id || 'coordinator'}`}
            />
          </div>
        </div>
      </div>

      {/* Workers */}
      <div className="ac-card">
        <div className="ac-card__header">
          <div>
            <div className="ac-badge">Worker Agents</div>
            <div className="ac-subtitle">Agents: {workers.length} / {MAX_WORKERS}</div>
          </div>
          <div className="ac-toolbar">
            <button
              className="ac-btn ac--primary"
              onClick={handleCreateWorker}
              disabled={workers.length >= MAX_WORKERS}
              type="button"
            >
              Create agent
            </button>
          </div>
        </div>
        <div className="ac-workers-grid">
          {workers.map((worker) => (
            <WorkerCardMini
              key={worker.id}
              worker={worker}
              availableModels={availableModels}
              integrationId={integrationId}
              isNameDuplicated={duplicateAgentNameKeys.has(
                String(worker.name || '').trim().toLowerCase()
              )}
              onUpdate={handleUpdateWorker}
              onDelete={handleDeleteWorker}
            />
          ))}
          {workers.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#5B7893', fontSize: '14px' }}>
              No workers. Click "Create agent" to add one (optional, up to {MAX_WORKERS}).
            </div>
          )}
        </div>
      </div>

      <div className="ac-card" style={{ marginTop: '16px', marginBottom: '16px' }}>
        <div className="ac-field" style={{ padding: '16px' }}>
          <label>RAG meta prompt</label>
          <textarea
            placeholder="Shared instructions for retrieval / RAG context (used when Use RAG is enabled on coordinator or workers)…"
            value={value?.metaPrompts?.RAG_PROMPT ?? ''}
            maxLength={MAX_AGENT_PROMPT_LENGTH}
            aria-describedby="mac-rag-meta-count"
            onChange={(e) => handleRagMetaPromptChange(e.target.value)}
            rows={4}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
          <div id="mac-rag-meta-count">
            <FieldCharCount length={(value?.metaPrompts?.RAG_PROMPT ?? '').length} max={MAX_AGENT_PROMPT_LENGTH} />
          </div>
        </div>
      </div>

      <div className="ac-card" style={{ marginBottom: 0 }}>
        <div className="ac-field" style={{ padding: '16px' }}>
          <label>
            Final step prompt
            <span style={{ fontWeight: 400, color: '#5B7893', fontSize: 12, marginLeft: 8 }}>
              (FINAL_STEP_PROMPT)
            </span>
          </label>
          <textarea
            placeholder={DEFAULT_FINAL_STEP_PROMPT}
            value={value?.metaPrompts?.FINAL_STEP_PROMPT ?? ''}
            maxLength={MAX_AGENT_PROMPT_LENGTH}
            aria-describedby="mac-final-step-meta-count"
            onChange={(e) => handleFinalStepMetaPromptChange(e.target.value)}
            rows={4}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
          <div id="mac-final-step-meta-count">
            <FieldCharCount length={(value?.metaPrompts?.FINAL_STEP_PROMPT ?? '').length} max={MAX_AGENT_PROMPT_LENGTH} />
          </div>
        </div>
      </div>
    </div>
  );
};

interface WorkerCardMiniProps {
  worker: AgentWorker;
  availableModels: string[];
  integrationId?: string | null;
  isNameDuplicated?: boolean;
  onUpdate: (id: string, updates: Partial<AgentWorker>) => void;
  onDelete: (id: string) => void;
}

const WorkerCardMini: React.FC<WorkerCardMiniProps> = ({
  worker,
  availableModels,
  integrationId,
  isNameDuplicated = false,
  onUpdate,
  onDelete,
}) => {
  const modelLabels = useMemo(
    () => getConfiguratorModelLabels(integrationId ?? ''),
    [integrationId]
  );
  return (
    <div className="ac-card ac-worker">
      <div className="ac-card__header ac-worker-header">
        <div className="ac-title" style={{ gap: 12 }}>
          <span className="ac-chip ac-mode-chip ac--sync">Sync</span>
        </div>
        <div className="ac-worker-actions">
          <button
            className="ac-btn ac-w-delete"
            title="Delete"
            onClick={() => onDelete(worker.id)}
            type="button"
          >
            Delete
          </button>
        </div>
      </div>
      <div className="ac-grid ac--two ac-w-content">
        <div className={`ac-field${isNameDuplicated ? ' ac-field--error' : ''}`} style={{ gridColumn: '1 / -1' }}>
          <label>Name</label>
          <input
            type="text"
            placeholder="Agent name (A-Z, a-z, 0-9)"
            value={worker.name}
            maxLength={MAX_NAME_LENGTH}
            pattern={AGENT_NAME_REGEX.source}
            onChange={(e) => onUpdate(worker.id, { name: sanitizeAgentName(e.target.value) })}
          />
          {isNameDuplicated && (
            <div className="ac-field-error-hint">This name is duplicated. Use a unique agent name.</div>
          )}
          <FieldCharCount length={worker.name.length} max={MAX_NAME_LENGTH} />
        </div>
        <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
          <label>Description</label>
          <textarea
            placeholder="Brief description..."
            value={worker.description}
            maxLength={MAX_AGENT_DESCRIPTION_LENGTH}
            aria-describedby={`mac-worker-${worker.id}-description-count`}
            onChange={(e) =>
              onUpdate(worker.id, { description: e.target.value.slice(0, MAX_AGENT_DESCRIPTION_LENGTH) })
            }
            rows={2}
          />
          <div id={`mac-worker-${worker.id}-description-count`}>
            <FieldCharCount length={worker.description.length} max={MAX_AGENT_DESCRIPTION_LENGTH} />
          </div>
        </div>
        <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
          <label>Prompt</label>
          <textarea
            placeholder="Instructions..."
            value={worker.prompt}
            maxLength={MAX_AGENT_PROMPT_LENGTH}
            aria-describedby={`mac-worker-${worker.id}-prompt-count`}
            onChange={(e) => onUpdate(worker.id, { prompt: e.target.value.slice(0, MAX_AGENT_PROMPT_LENGTH) })}
            rows={3}
          />
          <div id={`mac-worker-${worker.id}-prompt-count`}>
            <FieldCharCount length={worker.prompt.length} max={MAX_AGENT_PROMPT_LENGTH} />
          </div>
        </div>
        <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
          <label>Reminder prompt</label>
          <textarea
            placeholder={"e.g. Start your reply with the phrase '…'"}
            value={worker.reminder_prompt ?? ''}
            maxLength={MAX_AGENT_PROMPT_LENGTH}
            aria-describedby={`mac-worker-${worker.id}-reminder-count`}
            onChange={(e) =>
              onUpdate(worker.id, {
                reminder_prompt: e.target.value.slice(0, MAX_AGENT_PROMPT_LENGTH) || undefined,
              })
            }
            rows={2}
          />
          <div id={`mac-worker-${worker.id}-reminder-count`}>
            <FieldCharCount length={(worker.reminder_prompt ?? '').length} max={MAX_AGENT_PROMPT_LENGTH} />
          </div>
        </div>
        <div className="ac-field">
          <label>Model</label>
          <select
            value={worker.model}
            onChange={(e) => {
              const m = e.target.value;
              onUpdate(worker.id, {
                model: m,
                models_reasoning: resolveModelsReasoning(m, worker.models_reasoning),
              });
            }}
          >
            {availableModels.map((model) => (
              <option key={model} value={model}>
                {formatConfiguratorModelLabel(model, modelLabels)}
              </option>
            ))}
          </select>
        </div>
        {(() => {
          const spec = getGpt5ModelSpec(worker.model);
          if (!spec) return null;
          const mr = resolveModelsReasoning(worker.model, worker.models_reasoning) ?? spec.defaultReasoning;
          return (
            <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
              <label>Reasoning</label>
              <p className="ac-gpt5-hint">{spec.hint}</p>
              <select
                value={mr}
                onChange={(e) => onUpdate(worker.id, { models_reasoning: e.target.value })}
              >
                {spec.reasoningOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {reasoningOptionLabel(opt, spec.defaultReasoning)}
                  </option>
                ))}
              </select>
            </div>
          );
        })()}
        {(() => {
          const wMr = resolveModelsReasoning(worker.model, worker.models_reasoning);
          const tempActive = shouldShowTemperature(worker.model, wMr);
          const inactiveHint = getTemperatureInactiveHint(worker.model, wMr);
          if (!getGpt5ModelSpec(worker.model)) {
            return (
              <div className="ac-field ac-temperature-field">
                <label>Temperature</label>
                <div className="ac-range">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={worker.temperature}
                    onChange={(e) =>
                      onUpdate(worker.id, { temperature: normalizeTemperature(parseFloat(e.target.value)) })
                    }
                  />
                  <div className="ac-value">
                    <span>{worker.temperature.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div
              className={`ac-field ac-temperature-field${tempActive ? '' : ' ac-temperature-field--inactive'}`}
            >
              <label>
                Temperature
                {!tempActive ? <span className="ac-temperature-field__badge">Not active</span> : null}
              </label>
              {!tempActive && inactiveHint ? (
                <p className="ac-temperature-field__hint">{inactiveHint}</p>
              ) : null}
              <div className="ac-range">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={worker.temperature}
                  disabled={!tempActive}
                  onChange={(e) =>
                    onUpdate(worker.id, { temperature: normalizeTemperature(parseFloat(e.target.value)) })
                  }
                />
                <div className="ac-value">
                  <span>{worker.temperature.toFixed(1)}</span>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
          <label
            style={{ fontSize: '14px', fontWeight: 500, color: '#4A6683', marginBottom: '8px', display: 'block' }}
          >
            Tools for this agent
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <RagToggleRow
              on={worker.use_rag === true}
              onToggle={() => {
                const next = !worker.use_rag;
                onUpdate(worker.id, {
                  use_rag: next,
                  ...(next ? {} : { rag_doc_ids: undefined }),
                });
              }}
            />
          </div>
          <RagDocIdsField
            integrationId={integrationId}
            useRag={worker.use_rag === true}
            ragDocIds={worker.rag_doc_ids}
            onChange={(ids) => onUpdate(worker.id, { rag_doc_ids: ids })}
            disabled={false}
            idSuffix={`worker-${worker.id}`}
          />
        </div>
      </div>
    </div>
  );
};

export default MiniAgentConfigurator;
